import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { Text } from 'components/primitives/typography';
import EditInquiryModal from 'components/modals/EditInquiryModal';
import useModal from 'components/remodal/v2/useModal';
import AssignCaptainModal from 'components/modals/AssignCaptainModal';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import { SnackbarKey } from 'notistack';
import Button from 'components/ui/Button/Button';
import snakeCase from 'lodash/snakeCase';
import useRudderStackTrack from 'hooks/useRudderStackTrack';
import { Flex, FlexCol } from '../../primitives/flex';
import { useGetBookingActions } from '../hooks/useGetBookingActions';

const SidebarRenterActions = ({ booking, boat }) => {
  const { enqueueSnackbar, closeSnackbar } = useLazySnackbar();
  const router = useRouter();
  const rudderTrack = useRudderStackTrack();

  const bookingActions = useGetBookingActions(booking.id);
  const canBeBookedAction = bookingActions?.actions?.find((action) => action.action_type === 'can_be_booked');
  const firstDisabledReason = canBeBookedAction?.disabled_reasons?.[0]?.description;

  const { modal: editInquiryModal, openModal: openEditInquiryModal } = useModal(
    <EditInquiryModal booking={booking} />,
    {
      isOnRight: true,
      trackingName: 'edit_inquiry_modal',
    }
  );

  const { modal: assignCaptainModal, openModal: openAssignCaptainModal } = useModal(
    <AssignCaptainModal bookingId={booking.id} />,
    { fullHeight: true, size: 'medium', trackingName: 'assign_captain_modal' }
  );

  const { is_captain_available } = booking;

  const tripDurationHours = dayjs(booking.trip_finish).diff(dayjs(booking.trip_start), 'hours');

  const openCaptainWidget = (snackBarKey: SnackbarKey) => {
    closeSnackbar(snackBarKey);
    openAssignCaptainModal();
  };

  const triggerCaptainUnavailable = async () => {
    const snackBarKey = await enqueueSnackbar(
      'The captain assigned is not available during the time selected. Please assign another captain.',
      {
        variant: 'error',
        autoHideDuration: 4000,
        onClick: () => openCaptainWidget(snackBarKey),
      }
    );

    return false;
  };

  const requestBookingAction = () => {
    if (booking.instant_bookable) {
      return {
        actionText: 'Instant Book',
        available: true,
        onClick: () => router.push(`/a/bookings/${booking.id}/book_now`),
      };
    }
    if (booking.state === 'initialized' || booking.state === 'pre_approved') {
      if (canBeBookedAction?.enabled) {
        if (booking.state === 'initialized') {
          return {
            actionText: boat.is_instant_book ? 'Instant book' : 'Request to book',
            available: true,
            onClick: () => {
              router.push(`/a/bookings/${booking.id}/book_now`);
            },
          };
        }
        if (booking.state === 'pre_approved') {
          return {
            actionText: 'Book now',
            available: true,
            onClick: () => {
              router.push(`/a/bookings/${booking.id}/book_now`);
            },
          };
        }
      } else {
        return {
          actionText: firstDisabledReason,
          available: true,
          onClick: () => null,
          textOnly: true,
          paragraph: true,
        };
      }
    }

    return { actionText: 'Instant Book', available: false, onClick: () => null };
  };
  const buttons = [
    {
      actionText: 'Leave review',
      available: ['concluded', 'ashore', 'disputed'].includes(booking.state) && booking.show_renter_leave_a_review,
      onClick: () => router.push(`/a/bookings/${booking.id}/review`),
    },
    {
      actionText: 'View booking details',
      available: booking.can_view_details && booking.state !== 'expired',
      onClick: () => {
        window.location.href = booking.details_url;
      },
    },
    {
      actionText: requestBookingAction().actionText,
      available: requestBookingAction().available,
      onClick: is_captain_available ? requestBookingAction().onClick : triggerCaptainUnavailable,
      textOnly: requestBookingAction().textOnly,
      paragraph: requestBookingAction().paragraph,
    },
    {
      actionText: 'Edit inquiry',
      available: tripDurationHours <= 8 && (booking.state === 'initialized' || booking.state === 'pre_approved'),
      variant: 'renter',
      outline: true,
      onClick: openEditInquiryModal,
    },
  ].filter((b) => b.available);

  const renderButton = (button, index) => {
    if (buttons.length > 2 && index === 2) {
      return (
        <Button
          fullWidth
          variant="renter"
          justText
          type="button"
          onClick={() => {
            rudderTrack('booking_action_sidebar', {
              bookingId: booking.id,
              bookingState: booking.state,
              action: snakeCase(button.actionText),
              location: 'renter_side_bar_action',
            });

            button.onClick();
          }}
          text={
            <Text fWeight="500" as="span">
              {button.actionText}
            </Text>
          }
        />
      );
    }
    return (
      <Button
        fullWidth
        variant="renter"
        text={
          button.textOnly ? (
            <Text fWeight="500" as="span">
              {button.actionText}
            </Text>
          ) : (
            button.actionText
          )
        }
        onClick={button.onClick}
        justText={button.textOnly}
        outline={button.outline || index !== 0}
      />
    );
  };

  return (
    <FlexCol gap="15px">
      {buttons.map((button, index) => (
        <Flex key={button.actionText}>{button.paragraph ? button.actionText : renderButton(button, index)}</Flex>
      ))}

      {editInquiryModal}
      {assignCaptainModal}
    </FlexCol>
  );
};

export default SidebarRenterActions;
